<template>
  <Modal
    v-model="visible"
    :title="modalTitle"
    @on-cancel="handleCancel"
    :mask-closable="false"
    width="700"
  >
    <div class="crowd-modal-content">
      <!-- 两栏布局 -->
      <div class="two-column-layout">
        <!-- 左侧：选择指标 -->
        <div class="left-panel">
          <div class="panel-title">选择指标</div>
          <div class="indicator-list">
            <div
              v-for="item in indicatorList"
              :key="item.key"
              :class="[
                'indicator-item',
                { active: isIndicatorSelected(item.key) }
              ]"
              @click="handleSelectIndicator(item)"
            >
              {{ item.label }}
            </div>
          </div>
        </div>

        <!-- 右侧：指标配置 -->
        <div class="right-panel">
          <div class="panel-title">指标配置</div>
          <div class="config-area">
            <!-- 空状态 -->
            <div v-if="selectedIndicators.length === 0" class="empty-state">
              <Icon type="ios-folder-open-outline" size="48" color="#c5c8ce" />
              <p>请在左侧选择指标</p>
            </div>

            <!-- 已选指标配置 -->
            <div v-else class="config-list">
              <div
                v-for="(item, index) in selectedIndicators"
                :key="item.key"
                class="config-row"
              >
                <!-- 用户注册时间 - 特殊控件 -->
                <template v-if="isRangeIndicator(item.key)">
                  <span class="config-label">{{ item.label }}</span>
                  <Select
                    v-model="item.compareType"
                    placeholder="请选择"
                    style="width: 100px; margin: 0 4px"
                    :disabled="readonly"
                    @on-change="() => handleCompareTypeChange(item)"
                  >
                    <Option :value="1">大于等于</Option>
                    <Option :value="2">小于等于</Option>
                    <Option :value="3">介于区间</Option>
                  </Select>
                  <template v-if="item.key === 'cumulativePayAmount'">
                    <div class="range-input-wrap amount-input-wrap">
                      <input
                        type="text"
                        :value="amountRawInputs[item.key + '_min'] === undefined ? (item.minValue === null || item.minValue === undefined ? '' : String(item.minValue)) : amountRawInputs[item.key + '_min']"
                        :placeholder="item.minPlaceholder"
                        class="ivu-input ivu-input-default"
                        :class="{ 'error-input': hasRangeError(item, 'min'), 'amount-raw-input': true }"
                        :disabled="readonly"
                        @input="(e) => handleAmountRawInput(item, 'min', e)"
                        @blur="(e) => handleAmountRawBlur(item, 'min', e)"
                      />
                      <div
                        v-if="hasRangeError(item, 'min')"
                        class="inline-error-text"
                      >
                        {{ getRangeErrorMessage(item, 'min') }}
                      </div>
                    </div>
                    <span v-if="item.compareType === 3" style="margin: 0 4px"
                      >~</span
                    >
                    <div v-if="item.compareType === 3" class="range-input-wrap amount-input-wrap">
                      <input
                        type="text"
                        :value="amountRawInputs[item.key + '_max'] === undefined ? (item.maxValue === null || item.maxValue === undefined ? '' : String(item.maxValue)) : amountRawInputs[item.key + '_max']"
                        :placeholder="item.maxPlaceholder"
                        class="ivu-input ivu-input-default"
                        :class="{ 'error-input': hasRangeError(item, 'max'), 'amount-raw-input': true }"
                        :disabled="readonly"
                        @input="(e) => handleAmountRawInput(item, 'max', e)"
                        @blur="(e) => handleAmountRawBlur(item, 'max', e)"
                      />
                      <div
                        v-if="hasRangeError(item, 'max')"
                        class="inline-error-text"
                      >
                        {{ getRangeErrorMessage(item, 'max') }}
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="range-input-wrap">
                      <InputNumber
                        v-model="item.minValue"
                        :min="0"
                        :placeholder="item.minPlaceholder"
                        :precision="0"
                        :step="1"
                        style="width: 90px; margin: 0 4px"
                        :class="{ 'error-input': hasRangeError(item, 'min') }"
                        :disabled="readonly"
                      />
                      <div
                        v-if="hasRangeError(item, 'min')"
                        class="inline-error-text"
                      >
                        {{ getRangeErrorMessage(item, 'min') }}
                      </div>
                    </div>
                    <span v-if="item.compareType === 3" style="margin: 0 4px"
                      >~</span
                    >
                    <div v-if="item.compareType === 3" class="range-input-wrap">
                      <InputNumber
                        v-model="item.maxValue"
                        :min="0"
                        :placeholder="item.maxPlaceholder"
                        :precision="0"
                        :step="1"
                        style="width: 90px; margin: 0 4px"
                        :class="{ 'error-input': hasRangeError(item, 'max') }"
                        :disabled="readonly"
                      />
                      <div
                        v-if="hasRangeError(item, 'max')"
                        class="inline-error-text"
                      >
                        {{ getRangeErrorMessage(item, 'max') }}
                      </div>
                    </div>
                  </template>
                  <Icon
                    v-if="!readonly"
                    type="ios-trash-outline"
                    size="20"
                    color="#ed4014"
                    class="delete-icon"
                    @click="handleRemoveIndicator(index)"
                  />
                </template>

                <!-- 付费类型 - 两级多选（未付费与已付费互斥） -->
                <template v-else-if="item.key === 'payType'">
                  <span class="config-label">{{ item.label }}</span>
                  <div class="paytype-cascader-wrap" style="flex: 1; margin: 0 10px">
                    <Dropdown
                      trigger="click"
                      placement="bottom-start"
                      transfer
                      :disabled="readonly"
                    >
                      <div
                        class="paytype-trigger"
                        :class="{ 'is-readonly': readonly, 'is-empty': !hasPayTypeValue(item) }"
                        @click="readonly ? $event.stopPropagation() : null"
                      >
                        <span class="paytype-trigger-text">
                          {{ formatPayTypeLabel(item) || '请选择' }}
                        </span>
                        <Icon type="ios-arrow-down" size="14" />
                      </div>
                      <DropdownMenu slot="list" class="paytype-dropdown">
                        <div
                          class="paytype-main-panel"
                          :class="{ 'has-sub': isPaidSelected(item) }"
                        >
                          <!-- 未付费 -->
                          <div
                            class="paytype-main-item"
                            :class="{
                              active: isUnpaidSelected(item),
                              disabled: isPaidSelected(item)
                            }"
                            @click.stop="handleMainUnpaidToggle(item)"
                          >
                            <Checkbox
                              :value="isUnpaidSelected(item)"
                              :disabled="readonly || isPaidSelected(item)"
                              @click.native.prevent
                            />
                            <span class="paytype-main-label">未付费</span>
                          </div>
                          <!-- 已付费 -->
                          <div
                            class="paytype-main-item"
                            :class="{
                              active: isPaidSelected(item),
                              disabled: isUnpaidSelected(item)
                            }"
                            @click.stop="handleMainPaidToggle(item)"
                          >
                            <Checkbox
                              :value="isPaidSelected(item)"
                              :disabled="readonly || isUnpaidSelected(item)"
                              @click.native.prevent
                            />
                            <span class="paytype-main-label">已付费</span>
                            <Tooltip
                              content="已付费表示用户在所选任一付费类型中产生过付费行为。默认包含全部付费类型，可根据需要勾选具体付费类型进行筛选。"
                              max-width="300"
                              transfer
                            >
                              <Icon
                                type="ios-help-circle-outline"
                                size="14"
                                color="#808695"
                                class="paytype-help-icon"
                              />
                            </Tooltip>
                            <Icon
                              type="ios-arrow-forward"
                              size="14"
                              class="paytype-arrow"
                              :class="{ visible: isPaidSelected(item) }"
                            />
                          </div>
                        </div>
                        <!-- 已付费二级子项 -->
                        <div
                          v-if="isPaidSelected(item)"
                          class="paytype-sub-panel"
                        >
                          <div
                            v-for="sub in PAY_TYPES_SUB_OPTIONS"
                            :key="sub.value"
                            class="paytype-sub-item"
                            @click.stop="!readonly && handleSubPayTypeToggle(item, sub.value)"
                          >
                            <Checkbox
                              :value="
                                item.payTypes && item.payTypes.includes(sub.value)
                              "
                              :disabled="readonly"
                              @click.native.prevent
                            />
                            <span>{{ sub.label }}</span>
                          </div>
                        </div>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                  <Icon
                    v-if="!readonly"
                    type="ios-trash-outline"
                    size="20"
                    color="#ed4014"
                    class="delete-icon"
                    @click="handleRemoveIndicator(index)"
                  />
                </template>

                <!-- 当日是否登录 - Radio -->
                <template v-else-if="item.key === 'todayLogin'">
                  <span class="config-label">{{ item.label }}</span>
                  <RadioGroup
                    v-model="item.value"
                    style="flex: 1; margin: 0 10px"
                  >
                    <Radio :label="1" :disabled="readonly">是</Radio>
                    <Radio :label="0" :disabled="readonly">否</Radio>
                  </RadioGroup>
                  <Icon
                    v-if="!readonly"
                    type="ios-trash-outline"
                    size="20"
                    color="#ed4014"
                    class="delete-icon"
                    @click="handleRemoveIndicator(index)"
                  />
                </template>

                <!-- 是否VIP - Radio -->
                <template v-else-if="item.key === 'vipUser'">
                  <span class="config-label">{{ item.label }}</span>
                  <RadioGroup
                    v-model="item.value"
                    style="flex: 1; margin: 0 10px"
                  >
                    <Radio :label="1" :disabled="readonly">是</Radio>
                    <Radio :label="0" :disabled="readonly">否</Radio>
                  </RadioGroup>
                  <Icon
                    v-if="!readonly"
                    type="ios-trash-outline"
                    size="20"
                    color="#ed4014"
                    class="delete-icon"
                    @click="handleRemoveIndicator(index)"
                  />
                </template>

                <!-- 操作设备 - Radio -->
                <template v-else-if="item.key === 'deviceSys'">
                  <span class="config-label">{{ item.label }}</span>
                  <RadioGroup
                    v-model="item.value"
                    style="flex: 1; margin: 0 10px"
                  >
                    <Radio :label="0" :disabled="readonly">iOS</Radio>
                    <Radio :label="1" :disabled="readonly">安卓</Radio>
                  </RadioGroup>
                  <Icon
                    v-if="!readonly"
                    type="ios-trash-outline"
                    size="20"
                    color="#ed4014"
                    class="delete-icon"
                    @click="handleRemoveIndicator(index)"
                  />
                </template>

                <!-- 国家 - MultiSelect -->
                <template v-else-if="item.key === 'countryCodes'">
                  <span class="config-label">{{ item.label }}</span>
                  <MultiCascader
                    :disabled="readonly"
                    style="
                      flex: 1;
                      margin: 0 10px;
                      max-width: calc(100% - 130px);
                    "
                    :data="countryLi"
                    v-model="item.value"
                  />
                  <Icon
                    v-if="!readonly"
                    type="ios-trash-outline"
                    size="20"
                    color="#ed4014"
                    class="delete-icon"
                    @click="handleRemoveIndicator(index)"
                  />
                </template>

                <!-- 语言 - MultiSelect -->
                <template v-else-if="item.key === 'langIds'">
                  <span class="config-label">{{ item.label }}</span>
                  <Select
                    v-model="item.value"
                    multiple
                    filterable
                    placeholder="请选择语言"
                    style="flex: 1; margin: 0 10px"
                    :disabled="readonly"
                  >
                    <Option
                      v-for="opt in langOptions"
                      :key="opt.id"
                      :value="opt.id"
                      >{{ opt.langName }}</Option
                    >
                  </Select>
                  <Icon
                    v-if="!readonly"
                    type="ios-trash-outline"
                    size="20"
                    color="#ed4014"
                    class="delete-icon"
                    @click="handleRemoveIndicator(index)"
                  />
                </template>

                <!-- 渠道 - MultiCascader -->
                <template v-else-if="item.key === 'channels'">
                  <span class="config-label">{{ item.label }}</span>
                  <MultiCascader
                    :disabled="readonly"
                    style="
                      flex: 1;
                      margin: 0 10px;
                      max-width: calc(100% - 130px);
                    "
                    :data="channelOptions"
                    v-model="item.value"
                    placeholder="请选择渠道"
                  />
                  <Icon
                    v-if="!readonly"
                    type="ios-trash-outline"
                    size="20"
                    color="#ed4014"
                    class="delete-icon"
                    @click="handleRemoveIndicator(index)"
                  />
                </template>

                <!-- 其他通用指标 - 单选Select -->
                <template v-else>
                  <span class="config-label">{{ item.label }}</span>
                  <Select
                    v-model="item.value"
                    placeholder="请选择"
                    style="flex: 1; margin: 0 10px"
                    :disabled="readonly"
                  >
                    <Option
                      v-for="opt in item.options"
                      :key="opt.dictValue"
                      :value="Number(opt.dictValue)"
                      >{{ opt.dictName }}</Option
                    >
                  </Select>
                  <Icon
                    v-if="!readonly"
                    type="ios-trash-outline"
                    size="20"
                    color="#ed4014"
                    class="delete-icon"
                    @click="handleRemoveIndicator(index)"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 人群包名称 -->
      <div class="name-input-area">
        <span class="name-label">人群包名称</span>
        <Input
          v-model="formData.name"
          placeholder="请输入人群包名称"
          :disabled="readonly"
          :maxlength="100"
          style="flex: 1"
        />
      </div>
    </div>

    <template #footer v-if="!readonly">
      <Button @click="handleCancel">取消</Button>
      <Button type="primary" :loading="loading" @click="handleSubmit">{{
        formData.id ? '保存' : '新增'
      }}</Button>
    </template>
  </Modal>
</template>

<script>
import { addCrowd, editCrowd } from '@/api/crowd'
import { getDictionaryList } from '@/api/dictionary'
import { getLangList } from '@/api/lang'
import MultiCascader from '@/components/MultiCascader'

const USER_MONETIZATION_OPTIONS = [
  { dictValue: 1, dictName: 'IAA' },
  { dictValue: 2, dictName: 'IAP' }
]

const PAY_TYPES_OPTIONS = [
  { value: -1, label: '未付费' },
  { value: 0, label: '金币付费' },
  { value: 1, label: 'VIP付费' },
  { value: 2, label: '通行证付费' }
]

// 二级付费类型（不含“未付费”，只在已付费下出现）
const PAY_TYPES_SUB_OPTIONS = [
  { value: 0, label: '金币付费' },
  { value: 1, label: 'VIP付费' },
  { value: 2, label: '通行证付费' }
]

const RANGE_INDICATOR_CONFIG = {
  registerTime: {
    label: '用户注册时间',
    compareTypeField: 'registerTimeCompareType',
    minField: 'registerTimeMin',
    maxField: 'registerTimeMax',
    minPlaceholder: '最小值（小时）',
    maxPlaceholder: '最大值（小时）'
  },
  watchAdNum: {
    label: '看广告次数',
    compareTypeField: 'watchAdNumCompareType',
    minField: 'watchAdNumMin',
    maxField: 'watchAdNumMax',
    minPlaceholder: '最小值（次）',
    maxPlaceholder: '最大值（次）'
  },
  watchDuration: {
    label: '总观看时长',
    compareTypeField: 'watchDurationCompareType',
    minField: 'watchDurationMin',
    maxField: 'watchDurationMax',
    minPlaceholder: '最小值（小时）',
    maxPlaceholder: '最大值（小时）'
  },
  continuousLoginDays: {
    label: '连续登录天数',
    compareTypeField: 'continuousLoginDaysCompareType',
    minField: 'continuousLoginDaysMin',
    maxField: 'continuousLoginDaysMax',
    minPlaceholder: '最小值（天）',
    maxPlaceholder: '最大值（天）'
  },
  cumulativePayAmount: {
    label: '累充额度',
    compareTypeField: 'rechargeAmountCompareType',
    minField: 'rechargeAmountMin',
    maxField: 'rechargeAmountMax',
    minPlaceholder: '最小值（USD）',
    maxPlaceholder: '最大值（USD）'
  }
}

const RANGE_INDICATOR_KEYS = Object.keys(RANGE_INDICATOR_CONFIG)

export default {
  name: 'CrowdEditModal',
  components: {
    MultiCascader
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: () => ({})
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      payTypeOptions: [],
      PAY_TYPES_OPTIONS,
      PAY_TYPES_SUB_OPTIONS,
      langOptions: [],
      channelOptions: [],
      indicatorList: [
        { key: 'countryCodes', label: '国家' },
        { key: 'langIds', label: '语言' },
        { key: 'registerTime', label: '用户注册时间' },
        {
          key: 'userType',
          label: '用户类型',
          options: USER_MONETIZATION_OPTIONS
        },
        { key: 'payType', label: '付费类型', dictTopic: 'PAY_TYPE' },
        { key: 'deviceSys', label: '操作设备' },
        { key: 'channels', label: '渠道' },
        { key: 'todayLogin', label: '当日是否登录' },
        { key: 'watchAdNum', label: '看广告次数' },
        { key: 'watchDuration', label: '总观看时长' },
        { key: 'continuousLoginDays', label: '连续登录天数' },
        { key: 'vipUser', label: '是否VIP' },
        { key: 'cumulativePayAmount', label: '累充额度' }
      ],
      selectedIndicators: [],
      countryLi: [],
      rangeFieldErrors: {},
      amountRawInputs: {},
      formData: {
        name: ''
      }
    }
  },
  computed: {
    modalTitle() {
      if (this.readonly) return '查看人群包'
      return (this.formData.id ? '编辑' : '新增') + '人群包'
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fetchDictionaries()
            .then(() => {
              if (this.data.id) {
                this.mapDataToForm(this.data)
              } else {
                this.resetFormData()
              }
            })
            .finally(() => {
              this.visible = val
            })
        }
      }
    },
    visible(val) {
      if (!val) {
        // deep reset when closing the modal, to clear retained objects/arrays
        this.resetFormData()
      }
      this.$emit('input', val)
    }
  },
  methods: {
    async fetchDictionaries() {
      try {
        const [
          payRes,
          langRes,
          countryRes,
          channelChildRes,
          channelParentRes
        ] =
          await Promise.all([
            getDictionaryList({ dictTopic: 'PAY_TYPE' }),
            getLangList({ current: -1, size: -1 }),
            getDictionaryList({ dictTopic: 'GOODS_COUNTRY_GROUP' }),
            getDictionaryList({ dictTopic: 'COLORATION_CHANNEL' }),
            getDictionaryList({ dictTopic: 'COLORATION_CHANNEL_TYPE' })
          ])
        this.payTypeOptions = payRes.data.records || []
        this.langOptions = langRes.data.records || []

        let countryData = []
        if (countryRes.data && countryRes.data.records) {
          countryData = countryRes.data.records.map((i) => {
            return {
              label: i.dictName,
              value: i.dictName,
              children: i.dictValue.split(',').map((j) => {
                return {
                  value: j.split('|')[0],
                  label: j.split('|')[1]
                }
              })
            }
          })
        }
        this.countryLi = [...countryData]

        let channelData = []
        const channelChildren =
          channelChildRes.data && channelChildRes.data.records
            ? channelChildRes.data.records
            : []
        const channelParents =
          channelParentRes.data && channelParentRes.data.records
            ? channelParentRes.data.records
            : []

        if (channelParents.length && channelChildren.length) {
          channelData = channelParents.map((parent) => {
            let childValues = []
            try {
              childValues = JSON.parse(parent.dictValue)
            } catch (e) {
              // Ignore parse error
            }

            const children = childValues.map((val) => {
              const childInfo = channelChildren.find(
                (c) => c.dictValue === String(val)
              )
              return {
                label: childInfo ? childInfo.dictName : val,
                value: String(val)
              }
            })

            return {
              label: parent.dictName,
              value: parent.dictName,
              children: children.length ? children : undefined
            }
          })
        } else if (channelChildren.length) {
          // Fallback if parents are missing
          channelData = channelChildren.map((i) => ({
            label: i.dictName,
            value: i.dictValue
          }))
        }

        this.channelOptions = channelData

        // 更新 indicatorList 中单选字典项的 options
        const payIndicator = this.indicatorList.find((i) => i.key === 'payType')
        if (payIndicator) payIndicator.options = this.payTypeOptions
      } catch (e) {
        this.$Message.error('获取字典数据失败')
      }
    },
    isIndicatorSelected(key) {
      return this.selectedIndicators.some((item) => item.key === key)
    },
    hasPayTypeValue(item) {
      return item.payTypes && item.payTypes.length > 0
    },
    isUnpaidSelected(item) {
      return item.payTypes && item.payTypes.includes(-1)
    },
    isPaidSelected(item) {
      if (!item.payTypes || !item.payTypes.length) return false
      return item.payTypes.some(
        (v) => PAY_TYPES_SUB_OPTIONS.some((s) => s.value === v)
      )
    },
    handleMainUnpaidToggle(item) {
      if (this.readonly) return
      // 已选中其他付费类型时，未付费不可点
      if (this.isPaidSelected(item)) return
      const list = item.payTypes ? [...item.payTypes] : []
      const idx = list.indexOf(-1)
      if (idx >= 0) {
        list.splice(idx, 1)
      } else {
        list.push(-1)
      }
      this.$set(item, 'payTypes', list)
    },
    handleMainPaidToggle(item) {
      if (this.readonly) return
      // 已选中未付费时，已付费不可点
      if (this.isUnpaidSelected(item)) return
      const list = item.payTypes ? [...item.payTypes] : []
      // 只要子项非空，就视作已付费已选中；切换主项等价于全选/清空子项
      if (this.isPaidSelected(item)) {
        // 取消已付费：清空所有子项
        const cleared = list.filter((v) => v === -1)
        this.$set(item, 'payTypes', cleared)
      } else {
        // 选中已付费：默认全选子项
        const merged = list.filter((v) => v === -1)
        PAY_TYPES_SUB_OPTIONS.forEach((s) => merged.push(s.value))
        this.$set(item, 'payTypes', merged)
      }
    },
    handleSubPayTypeToggle(item, subValue) {
      if (this.readonly) return
      const list = item.payTypes ? [...item.payTypes] : []
      // 先确保不含 -1（因为子项与 -1 互斥）
      const idxUnpaid = list.indexOf(-1)
      if (idxUnpaid >= 0) list.splice(idxUnpaid, 1)
      const idx = list.indexOf(subValue)
      if (idx >= 0) {
        list.splice(idx, 1)
      } else {
        list.push(subValue)
      }
      this.$set(item, 'payTypes', list)
    },
    formatPayTypeLabel(item) {
      if (!item.payTypes || !item.payTypes.length) return ''
      if (item.payTypes.includes(-1)) return '未付费'
      const subs = item.payTypes
        .map((v) => {
          const sub = PAY_TYPES_SUB_OPTIONS.find((s) => s.value === v)
          return sub ? sub.label : ''
        })
        .filter(Boolean)
      if (subs.length) return `已付费（${subs.join('、')}）`
      return ''
    },
    isRangeIndicator(key) {
      return RANGE_INDICATOR_KEYS.includes(key)
    },
    createRangeIndicatorItem(key) {
      const config = RANGE_INDICATOR_CONFIG[key]
      if (!config) return null
      return {
        key,
        label: config.label,
        compareType: 1,
        minValue: null,
        maxValue: null,
        minPlaceholder: config.minPlaceholder,
        maxPlaceholder: config.maxPlaceholder
      }
    },
    appendRangeIndicatorFromData(data, key) {
      const config = RANGE_INDICATOR_CONFIG[key]
      if (!config) return
      const compareType = data[config.compareTypeField]
      if (compareType === null || compareType === undefined) return
      this.selectedIndicators.push({
        key,
        label: config.label,
        compareType,
        minValue: data[config.minField],
        maxValue: data[config.maxField],
        minPlaceholder: config.minPlaceholder,
        maxPlaceholder: config.maxPlaceholder
      })
    },
    applyRangeIndicatorToSubmit(submitData, item) {
      const config = RANGE_INDICATOR_CONFIG[item.key]
      if (!config) return
      submitData[config.compareTypeField] = item.compareType
      submitData[config.minField] = item.minValue
      if (item.compareType === 3) {
        submitData[config.maxField] = item.maxValue
      }
    },
    clearRangeError(item, field, type) {
      const key = item.key + '_' + field
      if (type === 'order') {
        if (this.rangeFieldErrors[key] && this.rangeFieldErrors[key]._order) {
          this.$delete(this.rangeFieldErrors, key)
        }
        return
      }
      if (this.rangeFieldErrors[key] && this.rangeFieldErrors[key]._nonOrder) {
        this.$delete(this.rangeFieldErrors, key)
      } else if (!type) {
        if (this.rangeFieldErrors[key]) {
          this.$delete(this.rangeFieldErrors, key)
        }
      }
    },
    setRangeError(item, field, message, type) {
      const key = item.key + '_' + field
      const payload = { message, _order: type === 'order', _nonOrder: type !== 'order' }
      // 非 order 错误 优先覆盖
      if (type !== 'order') {
        this.$set(this.rangeFieldErrors, key, payload)
      } else if (!this.rangeFieldErrors[key] || !this.rangeFieldErrors[key]._nonOrder) {
        // order 错误只在没有其他非 order 错误时才设置
        this.$set(this.rangeFieldErrors, key, payload)
      }
    },
    getRangeErrorMessage(item, field) {
      const err = this.rangeFieldErrors[item.key + '_' + field]
      return err ? err.message : ''
    },
    hasRangeError(item, field) {
      return !!this.getRangeErrorMessage(item, field)
    },
    validateRangeOrder(item) {
      const key = item.key
      // 只有累充额度和其他范围型指标在介于区间 compareType === 3 才校验顺序
      // 但我们目前只有累充额度用了 rangeFieldErrors 做展示，其他指标没绑到 class
      // 所以先只在累充额度上做展示
      if (item.compareType !== 3) {
        this.clearRangeError(item, 'min', 'order')
        this.clearRangeError(item, 'max', 'order')
        return
      }
      const minStr = this.amountRawInputs[key + '_min']
      const maxStr = this.amountRawInputs[key + '_max']
      const minNum = this.parseAmountToValidNumber(
        minStr === undefined
          ? item.minValue === null || item.minValue === undefined
            ? ''
            : String(item.minValue)
          : minStr
      )
      const maxNum = this.parseAmountToValidNumber(
        maxStr === undefined
          ? item.maxValue === null || item.maxValue === undefined
            ? ''
            : String(item.maxValue)
          : maxStr
      )
      if (
        minNum === null ||
        maxNum === null ||
        isNaN(minNum) ||
        isNaN(maxNum) ||
        minNum < 0 ||
        maxNum < 0
      ) {
        this.clearRangeError(item, 'min', 'order')
        this.clearRangeError(item, 'max', 'order')
        return
      }
      if (maxNum < minNum) {
        if (!this.rangeFieldErrors[key + '_max'] || !this.rangeFieldErrors[key + '_max']._nonOrder) {
          this.setRangeError(item, 'max', `最大值需大于等于最小值`, 'order')
        }
        this.clearRangeError(item, 'min', 'order')
      } else {
        this.clearRangeError(item, 'min', 'order')
        this.clearRangeError(item, 'max', 'order')
      }
    },
    handleCompareTypeChange(item) {
      if (item.key === 'cumulativePayAmount') {
        this.validateRangeOrder(item)
      }
    },
    parseAmountToValidNumber(str) {
      if (str === '' || str === null || str === undefined) return null
      if (!/^-?\d*\.?\d*$/.test(str)) return NaN
      return Number(str)
    },
    checkAmountDecimals(item, field, rawValue) {
      if (item.key !== 'cumulativePayAmount') {
        this.clearRangeError(item, field, 'non-order')
        return true
      }
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        this.clearRangeError(item, field, 'non-order')
        return true
      }
      const str = String(rawValue)
      if (!str.includes('.')) {
        this.clearRangeError(item, field, 'non-order')
        return true
      }
      const decimals = str.split('.')[1].length
      if (decimals > 2) {
        const label = field === 'min' ? '最小值' : '最大值'
        const msg = `最多支持两位小数`
        this.setRangeError(item, field, msg, 'non-order')
        return false
      }
      this.clearRangeError(item, field, 'non-order')
      return true
    },
    handleAmountRawInput(item, field, e) {
      const raw = e && e.target ? e.target.value : ''
      this.$set(this.amountRawInputs, item.key + '_' + field, raw)
      this.checkAmountDecimals(item, field, raw)
      const num = this.parseAmountToValidNumber(raw)
      if (!isNaN(num) && num !== null && num >= 0) {
        this.$set(item, field === 'min' ? 'minValue' : 'maxValue', num)
      } else if (raw === '') {
        this.$set(item, field === 'min' ? 'minValue' : 'maxValue', null)
      }
      this.validateRangeOrder(item)
    },
    handleAmountRawBlur(item, field, e) {
      const raw = e && e.target ? e.target.value : ''
      if (raw === '' || raw === null || raw === undefined) {
        this.clearRangeError(item, field, 'non-order')
        this.$set(this.amountRawInputs, item.key + '_' + field, undefined)
        this.$set(item, field === 'min' ? 'minValue' : 'maxValue', null)
        this.validateRangeOrder(item)
        return
      }
      const negative = raw.startsWith('-')
      let body = raw.replace(/^-/, '')
      if (!/^\d*\.?\d*$/.test(body)) body = ''
      const cleaned = (negative && body ? '-' : '') + body
      this.$set(this.amountRawInputs, item.key + '_' + field, cleaned || '')
      const decimalsOk = this.checkAmountDecimals(item, field, cleaned)
      const num = this.parseAmountToValidNumber(cleaned)
      if (!isNaN(num) && num !== null) {
        if (num < 0) {
          const label = field === 'min' ? '最小值' : '最大值'
          const msg = `${item.label}${label}不可为负数`
          this.setRangeError(item, field, msg, 'non-order')
        } else {
          this.$set(item, field === 'min' ? 'minValue' : 'maxValue', num)
        }
      } else {
        this.$set(item, field === 'min' ? 'minValue' : 'maxValue', null)
      }
      if (!decimalsOk || (num !== null && !isNaN(num) && num < 0)) {
        this.clearRangeError(item, field, 'order')
      }
      this.validateRangeOrder(item)
    },
    handleAmountPrecisionCheck() {},
    handleAmountBlurCheck() {},
    handleSelectIndicator(item) {
      if (this.readonly) return
      if (this.isIndicatorSelected(item.key)) return

      const newItem = this.isRangeIndicator(item.key)
        ? this.createRangeIndicatorItem(item.key)
        : { key: item.key, label: item.label }

      if (!this.isRangeIndicator(item.key)) {
        switch (item.key) {
          case 'countryCodes':
          case 'langIds':
          case 'channels':
            newItem.value = []
            break
          case 'deviceSys':
            newItem.value = 0
            break
          case 'todayLogin':
            newItem.value = 1
            break
          case 'payType':
            newItem.payTypes = []
            break
          case 'userType':
            newItem.options = USER_MONETIZATION_OPTIONS
            newItem.value = null
            break
          case 'vipUser':
            newItem.value = 0
            break
          default:
            newItem.value = null
        }
      }

      this.selectedIndicators.push(newItem)
    },
    handleRemoveIndicator(index) {
      const item = this.selectedIndicators[index]
      if (item) {
        this.clearRangeError(item, 'min')
        this.clearRangeError(item, 'max')
        this.$delete(this.amountRawInputs, item.key + '_min')
        this.$delete(this.amountRawInputs, item.key + '_max')
      }
      this.selectedIndicators.splice(index, 1)
    },
    mapDataToForm(data) {
      this.formData = {
        id: data.id,
        name: data.name
      }

      this.selectedIndicators = []

      // 国家
      if (data.countryCodes && data.countryCodes.length) {
        this.selectedIndicators.push({
          key: 'countryCodes',
          label: '国家',
          value: [...data.countryCodes]
        })
      }

      // 语言
      if (data.langIds && data.langIds.length) {
        this.selectedIndicators.push({
          key: 'langIds',
          label: '语言',
          value: [...data.langIds]
        })
      }

      this.appendRangeIndicatorFromData(data, 'registerTime')

      if (data.userType !== null && data.userType !== undefined) {
        this.selectedIndicators.push({
          key: 'userType',
          label: '用户类型',
          options: USER_MONETIZATION_OPTIONS,
          value: Number(data.userType)
        })
      }

      // 直接依据 payTypes 回显（多选，兼容历史数据）
      if (data.payTypes && data.payTypes.length) {
        this.selectedIndicators.push({
          key: 'payType',
          label: '付费类型',
          payTypes: [...data.payTypes]
        })
      }

      // 操作设备
      if (data.deviceSys !== null && data.deviceSys !== undefined) {
        this.selectedIndicators.push({
          key: 'deviceSys',
          label: '操作设备',
          value: data.deviceSys
        })
      }

      // 渠道
      if (data.channels && data.channels.length) {
        this.selectedIndicators.push({
          key: 'channels',
          label: '渠道',
          value: [...data.channels]
        })
      }

      if (data.todayLogin !== null && data.todayLogin !== undefined) {
        this.selectedIndicators.push({
          key: 'todayLogin',
          label: '当日是否登录',
          value: Number(data.todayLogin)
        })
      }

      this.appendRangeIndicatorFromData(data, 'watchAdNum')
      this.appendRangeIndicatorFromData(data, 'watchDuration')
      this.appendRangeIndicatorFromData(data, 'continuousLoginDays')
      this.appendRangeIndicatorFromData(data, 'cumulativePayAmount')

      if (data.vipUser !== null && data.vipUser !== undefined) {
        this.selectedIndicators.push({
          key: 'vipUser',
          label: '是否VIP',
          value: Number(data.vipUser)
        })
      }
    },
    resetFormData() {
      this.formData = {
        id: '',
        name: ''
      }
      this.selectedIndicators = []
      this.rangeFieldErrors = {}
      this.amountRawInputs = {}
    },
    handleCancel() {
      this.visible = false
    },
    handleSubmit() {
      if (!this.formData.name || !this.formData.name.trim()) {
        this.$Message.warning('请输入人群包名称')
        return
      }

      if (this.selectedIndicators.length === 0) {
        this.$Message.warning('指标不能为空，请至少配置 1 个指标')
        return
      }

      for (const item of this.selectedIndicators) {
        if (this.isRangeIndicator(item.key)) {
          if (item.minValue === null || item.minValue === undefined) {
            this.$Message.warning(`请填写${item.label}最小值`)
            return
          }
          if (Number(item.minValue) < 0) {
            this.$Message.warning(`${item.label}最小值不可为负数`)
            return
          }
          if (
            item.compareType === 3 &&
            (item.maxValue === null || item.maxValue === undefined)
          ) {
            this.$Message.warning(`请填写${item.label}最大值`)
            return
          }
          if (item.compareType === 3 && Number(item.maxValue) < 0) {
            this.$Message.warning(`最大值不可为负数`)
            return
          }
          if (item.key === 'cumulativePayAmount') {
            const minStr = String(item.minValue)
            const afterDot = minStr.includes('.') ? minStr.split('.')[1].length : 0
            if (afterDot > 2) {
              this.$Message.warning(`最多支持两位小数`)
              return
            }
            if (item.compareType === 3) {
              const maxStr = String(item.maxValue)
              const afterDotMax = maxStr.includes('.') ? maxStr.split('.')[1].length : 0
              if (afterDotMax > 2) {
                this.$Message.warning(`最多支持两位小数`)
                return
              }
            }
          }
          if (item.compareType === 3 && Number(item.maxValue) < Number(item.minValue)) {
            this.$Message.warning(`最大值需大于等于最小值，请重新输入`)
            return
          }
        } else if (
          item.key === 'countryCodes' ||
          item.key === 'langIds' ||
          item.key === 'channels'
        ) {
          if (!item.value || !item.value.length) {
            this.$Message.warning(`请至少选择一个${item.label}`)
            return
          }
        } else if (item.key === 'payType') {
          if (!item.payTypes || !item.payTypes.length) {
            this.$Message.warning(`请选择${item.label}`)
            return
          }
        } else {
          if (item.value === null || item.value === undefined) {
            this.$Message.warning(`请选择${item.label}`)
            return
          }
        }
      }

      this.loading = true

      const submitData = {
        name: this.formData.name.trim()
      }

      if (this.formData.id) {
        submitData.id = this.formData.id
      }

      this.selectedIndicators.forEach((item) => {
        switch (item.key) {
          case 'registerTime':
          case 'watchAdNum':
          case 'watchDuration':
          case 'continuousLoginDays':
          case 'cumulativePayAmount':
            this.applyRangeIndicatorToSubmit(submitData, item)
            break
          case 'countryCodes':
          case 'langIds':
          case 'channels':
            submitData[item.key] = item.value
            break
          case 'todayLogin':
            submitData.todayLogin = !!item.value
            break
          case 'vipUser':
            submitData.vipUser = !!item.value
            break
          case 'payType':
            submitData.payTypes = item.payTypes || []
            break
          default:
            submitData[item.key] = item.value
        }
      })

      const apiFunc = this.formData.id ? editCrowd : addCrowd
      apiFunc(submitData)
        .then(() => {
          this.$Message.success('保存成功')
          this.visible = false
          this.$emit('on-success')
        })
        .catch(() => {
          this.$Message.error('保存失败')
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped lang="less">
.crowd-modal-content {
  .two-column-layout {
    display: flex;
    gap: 20px;
    min-height: 200px;
    margin-bottom: 20px;

    .panel-title {
      font-weight: bold;
      margin-bottom: 12px;
      color: #17233d;
    }

    .left-panel {
      width: 140px;
      flex-shrink: 0;

      .indicator-list {
        .indicator-item {
          padding: 8px 12px;
          border: 1px solid #dcdee2;
          border-radius: 4px;
          margin-bottom: 8px;
          cursor: pointer;
          transition: all 0.2s;
          background: #fff;

          &:hover {
            border-color: #2d8cf0;
            color: #2d8cf0;
          }

          &.active {
            border-color: #2d8cf0;
            background: #e8f4ff;
            color: #2d8cf0;
          }
        }
      }
    }

    .right-panel {
      flex: 1;
      border: 1px solid #e8eaec;
      border-radius: 4px;
      padding: 12px;
      background: #fafafa;

      .config-area {
        min-height: 120px;

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 120px;
          color: #c5c8ce;

          p {
            margin-top: 10px;
          }
        }

        .config-list {
          .config-row {
            display: flex;
            align-items: flex-start;
            padding: 8px 0;
            border-bottom: 1px dashed #e8eaec;

            &:last-child {
              border-bottom: none;
            }

            > .ivu-select {
              flex-shrink: 0;
            }
            > .ivu-date-picker {
              flex-shrink: 0;
            }
            .range-input-wrap {
              flex-shrink: 0;
            }

            .config-label {
              width: 100px;
              flex-shrink: 0;
              color: #515a6e;
              line-height: 32px;
              align-self: flex-start;
            }

            .delete-icon {
              cursor: pointer;
              flex-shrink: 0;
              transition: color 0.2s;
              align-self: flex-start;
              line-height: 32px;

              &:hover {
                color: #ff4d4f !important;
              }
            }
          }
        }
      }
    }
  }

  .name-input-area {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-top: 15px;
    border-top: 1px solid #e8eaec;

    .name-label {
      width: 100px;
      flex-shrink: 0;
      color: #515a6e;
    }
  }

  .range-input-wrap {
    display: inline-flex;
    flex-direction: column;
    vertical-align: middle;
    .inline-error-text {
      color: #ed4014;
      font-size: 12px;
      line-height: 1.4;
      margin: 2px 4px 0;
      min-height: 16px;
      white-space: nowrap;
    }
    .error-input /deep/ .ivu-input-number {
      border-color: #ed4014 !important;
      box-shadow: 0 0 0 2px rgba(237,64,20,0.1);
    }
  }
  .amount-input-wrap {
    .amount-raw-input {
      width: 90px;
      margin: 0 4px;
      height: 32px;
      border-radius: 4px;
      box-sizing: border-box;
      outline: none;
      &:focus {
        border-color: #2d8cf0;
        box-shadow: 0 0 0 2px rgba(45,140,240,0.15);
      }
    }
    .amount-raw-input.error-input {
      border-color: #ed4014 !important;
      box-shadow: 0 0 0 2px rgba(237,64,20,0.1) !important;
    }
  }

  // 付费类型级联菜单
  .paytype-cascader-wrap {
    width: 100%;

    .paytype-trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 6px 12px;
      min-height: 32px;
      border: 1px solid #dcdee2;
      border-radius: 4px;
      background: #fff;
      cursor: pointer;
      transition: border-color 0.2s;

      &:hover {
        border-color: #57a3f3;
      }

      &.is-empty {
        .paytype-trigger-text {
          color: #c5c8ce;
        }
      }

      &.is-readonly {
        cursor: not-allowed;
        background: #f8f8f8;
      }

      .paytype-trigger-text {
        flex: 1;
        color: #515a6e;
        font-size: 13px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}

// 级联下拉面板（放在 scoped 外以便 Dropdown transfer 生效）


.paytype-dropdown {
  padding: 0 !important;
  min-width: 320px;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;

  .paytype-main-panel {
    padding: 6px 0;

    &.has-sub {
      border-bottom: 1px solid #f0f0f0;
    }

    .paytype-main-item {
      display: flex;
      align-items: center;
      padding: 8px 14px;
      cursor: pointer;
      transition: background 0.15s;

      &:hover {
        background: #f5f7fa;
      }

      &.active {
        background: #ecf5ff;

        .paytype-main-label {
          color: #2d8cf0;
          font-weight: 500;
        }
      }

      &.disabled {
        cursor: not-allowed;
        opacity: 0.4;

        &:hover {
          background: transparent;
        }
      }

      .paytype-main-label {
        flex: 1;
        margin-left: 8px;
        color: #515a6e;
        font-size: 13px;
      }

      .paytype-help-icon {
        margin-left: 4px;
        vertical-align: middle;
      }

      .paytype-arrow {
        margin-left: 6px;
        color: #c5c8ce;
        transition: color 0.2s;

        &.visible {
          color: #2d8cf0;
        }
      }
    }
  }

  .paytype-sub-panel {
    background: #fafbfc;
    padding: 6px 14px 6px 28px;

    .paytype-sub-title {
      padding: 6px 14px 4px 0;
      font-size: 12px;
      color: #9ea7b4;
    }

    .paytype-sub-item {
      display: flex;
      align-items: center;
      padding: 6px 14px 6px 8px;
      cursor: pointer;
      transition: background 0.15s;

      &:hover {
        background: #f0f4fa;
      }

      span {
        margin-left: 8px;
        color: #515a6e;
        font-size: 13px;
      }
    }
  }
}
</style>